import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'

// These constants define the minimum lengths for title and body to trigger duplicate issue checks
// and send analytics events. These are currently arbitrary values and can be adjusted based on future data.
const MIN_TITLE_LENGTH_FOR_DUPLICATE_CHECK = 10
const MIN_BODY_LENGTH_FOR_DUPLICATE_CHECK = 25

/**
 * @deprecated Temporary analytics hook for duplicate issue detection research
 *
 * **WARNING: This hook is subject to change or complete removal without notice.**
 *
 * This hook is part of an experimental data collection initiative to inform the development
 * of semantic duplicate issue detection capabilities. It tracks user input patterns during
 * issue creation to better understand content characteristics and user behavior.
 *
 * @example
 * ```tsx
 * const trackAnalytics = useSemanticSearchAnalytics(repository)
 *
 * // Called when title or body content changes
 * trackAnalytics(issueTitle, issueBody)
 * ```
 *
 * **Implementation Notes:**
 * - Debounced by 800ms to reduce event frequency
 * - Only triggers when feature flag `issues_create_semantic_similarity_analytics` is enabled
 * - Requires minimum content length thresholds to be met
 * - Data collected: repository NWO, title length, body length
 *
 * **Future Plans:**
 * - May be replaced with a more comprehensive duplicate detection system
 * - Constants and thresholds will be adjusted based on collected data
 * - Hook may be removed entirely once research phase is complete
 *
 * @param repository - The repository context for issue creation
 * @returns Debounced analytics function that accepts (title: string, body: string)
 *
 * @internal This is an experimental feature for data collection purposes only!
 */
export const useSemanticSearchAnalytics = (repository: RepositoryPickerRepository) => {
  const {sendAnalyticsEvent} = useAnalytics()
  const isIssuesCreateSemanticSimilarityAnalyticsEnabled = isFeatureEnabled(
    'issues_create_semantic_similarity_analytics',
  )

  // Issue analytics should only run when:
  // 1. Feature flag `issues_create_semantic_similarity_analytics` is enabled, AND
  // 2. Title is at least MIN_TITLE_LENGTH_FOR_DUPLICATE_CHECK characters (${MIN_TITLE_LENGTH_FOR_DUPLICATE_CHECK}), OR
  // 3. Body is at least MIN_BODY_LENGTH_FOR_DUPLICATE_CHECK characters (${MIN_BODY_LENGTH_FOR_DUPLICATE_CHECK}), OR
  // 4. A paste event occurred, OR
  // 5. The content length changed by at least MIN_LENGTH_CHANGE_FOR_QUERY characters (${MIN_LENGTH_CHANGE_FOR_QUERY})
  return useDebounce(
    async (searchTitle: string, searchBody: string) => {
      // Only send analytics if feature flag is enabled and content meets minimum length requirements
      if (
        isIssuesCreateSemanticSimilarityAnalyticsEnabled &&
        (searchTitle.trim().length >= MIN_TITLE_LENGTH_FOR_DUPLICATE_CHECK ||
          searchBody.trim().length >= MIN_BODY_LENGTH_FOR_DUPLICATE_CHECK)
      ) {
        sendAnalyticsEvent('analytics.issue.content_update', 'SEMANTIC_SIMILAR_ISSUE_ANALYTICS', {
          repoNWO: `${repository.owner.login}/${repository.name}`,
          titleLength: searchTitle.length,
          bodyLength: searchBody.length,
        })
      }
    },
    800,
    {onChangeBehavior: 'cancel'},
  )
}
